import styles from 'css/src/components/common/mentions/ReferenceBrowserModal.module.scss';

import React from 'react';
import classNames from 'classnames';

import { KIND_ICON } from './referenceKinds';
import type { Reference, ReferenceKind } from './ReferenceMentionNode';
import { referencesFromVideos, VIDEO_PAGE_SIZE } from './videoReferences';

import { ApiService, Urls } from 'js/src/client';
import { useIsOnScreen } from 'js/src/components/common/hooks/useIsOnScreen';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import Spinner from 'js/src/components/common/ui/Spinner';
import { Tab, Tabs, TAB_SIZE, TAB_TITLE_POSITION } from 'js/src/components/common/ui/Tabs';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput from 'js/src/components/common/ui/TextInput';

const TABS: { kind: ReferenceKind; title: string }[] = [
    { kind: 'video', title: 'Your videos' },
    { kind: 'blueprint', title: 'Your blueprints' },
];

const MODAL_TITLE = 'Create similar video to...';
const MODAL_SUBTITLE =
    "Pick a video you'd like to create another video similar to. The AI will learn your style.";

const SEARCH_DEBOUNCE_MS = 300;

interface VideoPages {
    /** The search the loaded pages answer, so stale pages are never extended. */
    forSearch: string;
    references: Reference[];
    pagesLoaded: number;
    moreExist: boolean;
}

const NO_PAGES: VideoPages = { forSearch: '', references: [], pagesLoaded: 0, moreExist: false };

// A module function rather than part of the hook: React Compiler cannot model
// a try/finally and fails the whole hook over it.
async function fetchVideoPage(
    searchTerm: string,
    page: number,
    isCurrent: () => boolean,
    setPages: React.Dispatch<React.SetStateAction<VideoPages>>,
    setLoading: (loading: boolean) => void,
): Promise<void> {
    try {
        const { data } = await ApiService.videoList({
            query: {
                page,
                page_size: VIDEO_PAGE_SIZE,
                only_archived: false,
                sort_by: 'last_updated_date',
                ...(searchTerm && { search: searchTerm }),
            },
        });
        if (!isCurrent()) {
            return;
        }
        const loaded = referencesFromVideos(data?.results ?? []);
        setPages((previous) => ({
            forSearch: searchTerm,
            references: page === 1 ? loaded : [...previous.references, ...loaded],
            pagesLoaded: page,
            moreExist: Boolean(data?.next),
        }));
    } catch {
        if (!isCurrent()) {
            return;
        }
        setPages((previous) =>
            page === 1 ? { ...NO_PAGES, forSearch: searchTerm } : { ...previous, moreExist: false },
        );
    } finally {
        if (isCurrent()) {
            setLoading(false);
        }
    }
}

/**
 * Videos come from the server rather than the typeahead's preloaded page, so
 * search covers every video and scrolling loads the rest page by page.
 */
function useBrowsedVideos(browsing: boolean, search: string) {
    const [pages, setPages] = React.useState<VideoPages>(NO_PAGES);
    const [loading, setLoading] = React.useState(true);
    const generation = React.useRef(0);

    const loadPage = React.useCallback(async (searchTerm: string, page: number) => {
        const requestGeneration = ++generation.current;
        setLoading(true);
        await fetchVideoPage(
            searchTerm,
            page,
            () => requestGeneration === generation.current,
            setPages,
            setLoading,
        );
    }, []);

    React.useEffect(() => {
        if (browsing) {
            void loadPage(search, 1);
        }
    }, [browsing, search, loadPage]);

    const endOfListRef = React.useRef<HTMLDivElement>(null);
    const endOfListVisible = useIsOnScreen(endOfListRef);

    React.useEffect(() => {
        if (
            !browsing ||
            !endOfListVisible ||
            loading ||
            !pages.moreExist ||
            pages.forSearch !== search
        ) {
            return;
        }
        // The observer's report can be a frame stale after tiles append, so
        // confirm the end of the list is still within the scroll area before
        // asking for more.
        const end = endOfListRef.current;
        const scrollArea = end?.parentElement;
        if (
            end &&
            scrollArea &&
            end.getBoundingClientRect().top > scrollArea.getBoundingClientRect().bottom
        ) {
            return;
        }
        void loadPage(search, pages.pagesLoaded + 1);
    }, [browsing, endOfListVisible, loading, pages, search, loadPage]);

    return { videos: pages.references, loading, endOfListRef };
}

interface ReferenceBrowserModalProps {
    showing: boolean;
    references: Reference[];
    /** Which tab to open on, from the "See all" row or pill that opened it. */
    kind: ReferenceKind;
    /** Highlighted as current, when swapping an existing mention. */
    selectedId?: string | null;
    onSelect: (reference: Reference) => void;
    onHidden: () => void;
}

const BrowserModalContent: React.FC<ReferenceBrowserModalProps> = ({
    references,
    kind,
    selectedId,
    onSelect,
    onHidden,
}) => {
    const [query, setQuery] = React.useState('');
    const [activeKind, setActiveKind] = React.useState<ReferenceKind>(kind);

    const [search, setSearch] = React.useState('');
    React.useEffect(() => {
        const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    const browsingVideos = activeKind === 'video';
    const { videos, loading, endOfListRef } = useBrowsedVideos(browsingVideos, search);

    // Blueprints aren't paginated — the store holds every one the account has,
    // so matching them client-side already covers the full set.
    const blueprints = React.useMemo(
        () => references.filter((reference) => reference.kind === 'blueprint'),
        [references],
    );
    const blueprintMatches = React.useMemo(() => {
        const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
        return blueprints.filter((reference) =>
            words.every((word) => reference.name.toLowerCase().includes(word)),
        );
    }, [blueprints, query]);

    const matches = browsingVideos ? videos : blueprintMatches;
    const stillLoading = browsingVideos && loading;
    const kindIsEmpty = browsingVideos ? videos.length === 0 && !query : blueprints.length === 0;

    return (
        <Modal showing onHidden={onHidden} title={MODAL_TITLE} size={MODAL_SIZES.LG} scrollable>
            <p className={styles['reference-browser__subtitle']}>{MODAL_SUBTITLE}</p>

            <Tabs
                activeKey={activeKind}
                onSelect={(key) => setActiveKind((key as ReferenceKind | null) ?? 'video')}
                titlePosition={TAB_TITLE_POSITION.START}
                size={TAB_SIZE.SM}
            >
                {TABS.map((tab) => (
                    <Tab key={tab.kind} eventKey={tab.kind} title={tab.title} />
                ))}
            </Tabs>

            <div className="mt-l5-16">
                <TextInput
                    text={query}
                    onChange={(event: TypeInputChangeEvent) => setQuery(event.target.value)}
                    placeholder="Search"
                    autoFocus
                />
            </div>

            <div className={`${styles['reference-browser__grid']} my-l5-16`}>
                {matches.map((reference) => (
                    <button
                        type="button"
                        key={reference.id}
                        className={classNames(styles['reference-browser__tile'], {
                            [styles['selected']]: reference.id === selectedId,
                        })}
                        onClick={() => onSelect(reference)}
                    >
                        <span className={styles['reference-browser__thumb']}>
                            {reference.thumbUrl ? (
                                <img src={reference.thumbUrl} alt="" />
                            ) : (
                                <i className="material-symbols-outlined">
                                    {KIND_ICON[reference.kind]}
                                </i>
                            )}
                        </span>
                        <span className={styles['reference-browser__name']}>{reference.name}</span>
                        {reference.detail && (
                            <span className={styles['reference-browser__detail']}>
                                {reference.detail}
                            </span>
                        )}
                    </button>
                ))}

                {matches.length === 0 &&
                    !stillLoading &&
                    (kindIsEmpty ? (
                        <p className={styles['reference-browser__empty']}>
                            {activeKind === 'blueprint' ? (
                                <>
                                    You don’t have any blueprints yet.{' '}
                                    <a
                                        href={Urls.BLUEPRINTS_PAGE}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Create one
                                    </a>{' '}
                                    to reuse a video’s structure.
                                </>
                            ) : (
                                'You don’t have any videos yet.'
                            )}
                        </p>
                    ) : (
                        <p className={styles['reference-browser__empty']}>
                            Nothing matches “{query}”.
                        </p>
                    ))}

                {stillLoading && (
                    <span className={styles['reference-browser__loading']}>
                        <Spinner />
                    </span>
                )}
                <div ref={endOfListRef} className={styles['reference-browser__end']} />
            </div>
        </Modal>
    );
};

/**
 * The escape hatch from the typeahead's per-kind cap, for videos and blueprints.
 * Templates and voices go through their own app-wide pickers instead.
 * Mounted fresh each time it opens, so no state leaks between openings.
 */
const ReferenceBrowserModal: React.FC<ReferenceBrowserModalProps> = (props) => {
    return props.showing ? <BrowserModalContent {...props} /> : null;
};

export default ReferenceBrowserModal;
